import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormSetValue,
} from "react-hook-form";
import { Masks } from "react-native-mask-input";
import { z } from "zod";

import type { ProviderDetails } from "@/api/generated/models";
import {
  Checkbox,
  Grid,
  Input,
  MaskedInput,
  Select,
  YStack,
  getGridLayout,
} from "@/components";
import {
  AddressAutocomplete,
  type ParsedAddress,
} from "@/forms/AddressAutocomplete";
import { US_STATES } from "@/settings/constants";

export const providerSchema = z.object({
  fullName: z.string().trim().min(1, "Enter the provider's full name"),
  phone: z
    .string()
    .trim()
    .refine(
      (value) => !value || /^\d{10}$/.test(value),
      "Phone number must be 10 digits",
    ),
  streetAddress: z.string().trim(),
  streetAddress2: z.string().trim(),
  city: z.string().trim(),
  state: z.string().trim(),
  zipCode: z
    .string()
    .trim()
    .refine(
      (value) => !value || /^\d{5}$/.test(value),
      "Zip code must be 5 digits",
    ),
  sharingConsent: z.boolean(),
});
export type ProviderFormValues = z.infer<typeof providerSchema>;

export const EMPTY_PROVIDER: ProviderFormValues = {
  fullName: "",
  phone: "",
  streetAddress: "",
  streetAddress2: "",
  city: "",
  state: "",
  zipCode: "",
  sharingConsent: true,
};

export const providerPayload = (
  values: ProviderFormValues,
): ProviderDetails => ({
  status: "provided",
  full_name: values.fullName,
  phone: values.phone || null,
  street_address: values.streetAddress || null,
  street_address_2: values.streetAddress2 || null,
  city: values.city || null,
  state: values.state || null,
  zip_code: values.zipCode || null,
  sharing_consent: values.sharingConsent,
});

export const providerValues = (
  provider: ProviderDetails,
): ProviderFormValues => ({
  fullName: provider.full_name,
  phone: provider.phone ?? "",
  streetAddress: provider.street_address ?? "",
  streetAddress2: provider.street_address_2 ?? "",
  city: provider.city ?? "",
  state: provider.state ?? "",
  zipCode: provider.zip_code ?? "",
  sharingConsent: provider.sharing_consent,
});

export function ProviderFields({
  control,
  setValue,
  errors,
  role,
  readOnlyValues,
}: {
  control: Control<ProviderFormValues>;
  setValue: UseFormSetValue<ProviderFormValues>;
  errors: FieldErrors<ProviderFormValues>;
  role: "pcp" | "referrer";
  readOnlyValues?: ProviderFormValues;
}) {
  const readonly = Boolean(readOnlyValues);
  const nameLabel =
    role === "pcp" ? "Provider full name" : "Referring clinician full name";
  const selectAddress = (address: ParsedAddress) => {
    for (const name of [
      "streetAddress",
      "streetAddress2",
      "city",
      "state",
      "zipCode",
    ] as const) {
      setValue(name, address[name] ?? "", {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };
  return (
    <YStack pointerEvents={readonly ? "none" : "auto"} gap="$250">
      <Grid>
        <Controller
          control={control}
          name="fullName"
          render={({ field }) => (
            <Input.Controlled
              label={nameLabel}
              placeholder={nameLabel}
              {...field}
              value={readOnlyValues?.fullName ?? field.value}
              readonly={readonly}
              errorText={readonly ? undefined : errors.fullName?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <MaskedInput.Controlled
              ref={field.ref}
              name={field.name}
              label="Phone number"
              placeholder="Phone number"
              value={readOnlyValues?.phone ?? field.value}
              mask={Masks.USA_PHONE}
              onChangeText={(_, unmasked) => field.onChange(unmasked)}
              onBlur={field.onBlur}
              readonly={readonly}
              inputMode="tel"
              textContentType="telephoneNumber"
              errorText={readonly ? undefined : errors.phone?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="streetAddress"
          render={({ field }) =>
            readOnlyValues ? (
              <Input.Controlled
                ref={field.ref}
                name={field.name}
                label="Practice address"
                value={readOnlyValues.streetAddress}
                readonly
                rootProps={getGridLayout(6)}
              />
            ) : (
              <AddressAutocomplete
                ref={field.ref}
                name={field.name}
                label="Practice address"
                placeholder="Practice address"
                value={field.value}
                onChangeText={field.onChange}
                onSelectAddress={selectAddress}
                rootProps={getGridLayout(6)}
              />
            )
          }
        />
        <Controller
          control={control}
          name="streetAddress2"
          render={({ field }) => (
            <Input.Controlled
              label="Apartment, suite, etc."
              placeholder="Apartment, suite, etc."
              {...field}
              value={readOnlyValues?.streetAddress2 ?? field.value}
              readonly={readonly}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="city"
          render={({ field }) => (
            <Input.Controlled
              label="City"
              placeholder="City"
              {...field}
              value={readOnlyValues?.city ?? field.value}
              readonly={readonly}
              rootProps={getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="state"
          render={({ field }) => (
            <Select
              ref={field.ref}
              name={field.name}
              label="State"
              items={US_STATES}
              value={readOnlyValues?.state ?? field.value}
              onValueChange={field.onChange}
              placeholder="Select state"
              disabled={readonly}
              {...getGridLayout(6)}
            />
          )}
        />
        <Controller
          control={control}
          name="zipCode"
          render={({ field }) => (
            <Input.Controlled
              label="Zip code"
              placeholder="Zip code"
              {...field}
              value={readOnlyValues?.zipCode ?? field.value}
              readonly={readonly}
              keyboardType="numeric"
              autoComplete="postal-code"
              textContentType="postalCode"
              inputMode="numeric"
              maxLength={5}
              errorText={readonly ? undefined : errors.zipCode?.message}
              rootProps={getGridLayout(6)}
            />
          )}
        />
      </Grid>
      <Controller
        control={control}
        name="sharingConsent"
        render={({ field }) => (
          <Checkbox
            ref={field.ref}
            id={`${role}-care-updates`}
            checked={readOnlyValues?.sharingConsent ?? field.value}
            onCheckedChange={(checked) => field.onChange(checked === true)}
            disabled={readonly}
            label="I agree to Curai Health sharing care updates with this provider"
          />
        )}
      />
    </YStack>
  );
}
